import { type Crumb, Seo, StatusState, StructuredData } from '@/components/molecules';
import { ImportPromiseBand, PageIntro, QuoteBand } from '@/components/organisms';
import { DEFAULT_INTRO_SLIDES } from '@/config/introVehicles';
import { breadcrumbSchema } from '@/utils/structuredData';

import { CarsResults } from './organisms/CarsResults';
import { useCarsPage } from './useCarsPage';

/** /cars, /cars/:make and /cars/:make/:model (IA §2). */
/** Opens WhatsApp with the context of this page (§60 K/L). */
export const handle = {
  whatsappMessage: "Hello Kabex Motors, I'm looking at the cars on your website.",
};

export default function CarsPage() {
  const p = useCarsPage();

  if (p.notFound) {
    return (
      <>
        <Seo title="Page not found" description="This page does not exist." path="/404" noindex />
        <StatusState
          tone="empty"
          title="We couldn't find that make or model"
          description="We may not have listed that one yet. Browse all cars, or ask us to import it for you."
          action={{ label: 'Browse all cars', to: '/cars' }}
        />
      </>
    );
  }

  const makeLabel = p.makeName ?? p.makeParam;
  const modelLabel = p.modelName ?? p.modelParam;
  const title = p.modelParam
    ? `${makeLabel} ${modelLabel} for sale in Uganda`
    : p.makeParam
      ? `${makeLabel} cars for sale in Uganda`
      : 'Cars for sale in Uganda';
  const path = ['/cars', p.makeParam, p.modelParam].filter(Boolean).join('/');
  const crumbs: Crumb[] = [{ label: 'Cars', to: '/cars' }];
  if (p.makeParam && makeLabel) crumbs.push({ label: makeLabel, to: `/cars/${p.makeParam}` });
  if (p.modelParam && modelLabel) crumbs.push({ label: modelLabel });
  const isEmpty = p.list.data?.total === 0;

  return (
    <>
      <StructuredData data={breadcrumbSchema([{ label: 'Home', to: '/' }, ...crumbs])} />
      <Seo
        title={title}
        description={`Browse ${p.makeParam ? `${makeLabel} ` : ''}cars from Japan, the UK and worldwide with Kabex Motors. Request a quotation with your CIF destination and package services.`}
        path={path}
        // Filter combinations and empty pages are not worth indexing.
        noindex={p.hasQueryFilters || isEmpty}
      />
      <PageIntro
        slideshow
        compact
        keepOnPhone
        slides={DEFAULT_INTRO_SLIDES}
        eyebrow="Cars for sale"
        title={title}
        lede="Browse cars for sale, or ask us to source a specific model."
        {...(p.makeParam ? { crumbs } : {})}
      />
      <CarsResults
        formDefaults={p.formDefaults}
        sort={p.sort}
        page={p.page}
        pageCount={p.pageCount}
        data={p.list.data}
        isPending={p.list.isPending}
        isFetching={p.list.isFetching}
        error={p.list.error}
        onRetry={() => void p.list.refetch()}
      />
      <ImportPromiseBand />
      <QuoteBand />
    </>
  );
}
